import { ChangeEvent, useState, useEffect } from "react";
import { Icon, ToggleButton, ErrorFilledSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import AiSparkWithGradientIcon from "@/icons/AiSparkWithGradientIcon/AiSparkWithGradientIcon";

import {
  innerContentCss,
  innerBorderCss,
  rootContainerCss,
  svgCss,
  textAreaRootContainerCss,
  labelTextCss,
  textAreaCss,
  headingSectionExpandedCss,
  toggleSwitchContainerCss,
  footerSectionCss,
  charLimitContainerCss,
  charLimitIconCss,
  charLimitInfoCss,
  charLimitCss,
  charLimitSlotCss,
} from "./AIExpandableTextArea.styles";

interface AIExpandableTextAreaProps {
  expanded: boolean;
  handleExpandAndFocus: () => void;
  handleContainerBlur: (e: React.FocusEvent) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  isMobileDisplay?: boolean;
  inputValue: string;
  pressed: boolean;
  onToggle: () => void;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const MAX_CHARS = 400;

const ToggleWithTrackEvent = withTrackEvent(ToggleButton);

const textAreaDescriptor = "search-with-luna-textarea";

const searchInputExamples = [
  "3rd grade articles about the concept of community helpers",
  "Videos about how photosynthesis works",
  "Text sets explaining figurative language",
];

/**
 * Returns a random integer between 0 (inclusive) and `max` (exclusive).
 */
const getRandomInteger = (max: number) => Math.floor(Math.random() * max);

const AIExpandableTextArea = ({
  containerRef,
  expanded,
  handleExpandAndFocus,
  handleContainerBlur,
  isMobileDisplay,
  inputValue,
  textAreaRef,
  pressed,
  onToggle,
  onChange,
  onKeyDown,
}: AIExpandableTextAreaProps) => {
  const isCharacterLimitReached = inputValue.length === MAX_CHARS;

  const sparkleIcon = (
    <Icon
      SvgComponent={AiSparkWithGradientIcon}
      size={Icon.sizes.sm}
      isDecorative
      __cssFor={{ root: svgCss }}
    />
  );

  // This is a simple state to hold the index of the search examples
  // array. When the text area is expanded, we set a new value for the index
  // and render one of the strings of `searchInputExamples`.
  const [searchExampleIndex, setSearchExampleIndex] = useState(
    getRandomInteger(searchInputExamples.length),
  );
  // When the text area is expanded, we cycle through the list of search
  // examples.
  useEffect(() => {
    if (expanded === true && inputValue.length === 0) {
      setSearchExampleIndex(
        (searchExampleIndex + 1) % searchInputExamples.length,
      );
    }
  }, [expanded]);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      aria-expanded={expanded}
      aria-label="Search with Luna input"
      onFocus={handleExpandAndFocus}
      onBlur={handleContainerBlur}
      css={rootContainerCss(expanded, isMobileDisplay)}
      data-qa-selector="search-with-luna-toggle"
    >
      <div css={innerBorderCss(expanded, isMobileDisplay)}>
        <div css={innerContentCss(expanded)}>
          {expanded ? (
            <div css={headingSectionExpandedCss}>
              {sparkleIcon}
              <span>What are you looking for?</span>
            </div>
          ) : (
            sparkleIcon
          )}
          <div
            css={textAreaRootContainerCss(expanded, isCharacterLimitReached)}
          >
            <label htmlFor={textAreaDescriptor} css={labelTextCss}>
              Search with Luna
            </label>
            <textarea
              css={textAreaCss(expanded)}
              ref={textAreaRef}
              id={textAreaDescriptor}
              value={inputValue}
              maxLength={MAX_CHARS}
              placeholder={
                expanded
                  ? `For example: “${searchInputExamples[searchExampleIndex]}”`
                  : "What are you looking for?"
              }
              onChange={onChange}
              onKeyDown={onKeyDown}
              data-qa-selector={textAreaDescriptor}
              name="needle"
              rows={expanded ? 7 : 1}
            />
          </div>
          {expanded ? (
            <div
              css={charLimitSlotCss(isCharacterLimitReached)}
              aria-live="polite"
            >
              <div css={charLimitContainerCss}>
                <ErrorFilledSVG css={charLimitIconCss} />
                <div css={charLimitInfoCss}>
                  Character limit reached. Please shorten your text to search.
                </div>
              </div>
            </div>
          ) : null}
          {expanded ? (
            <div css={footerSectionCss}>
              <ToggleWithTrackEvent
                label="Disable Search with Luna"
                __cssFor={{
                  switchContainerStyles: pressed
                    ? toggleSwitchContainerCss
                    : "",
                }}
                dataProps={{
                  "data-qa-selector": "disable-search-with-luna-toggle",
                }}
                objectType={objectTypes.user}
                actionName={actionNames.interact}
                streamProperties={{
                  description: "Toggle Search with Luna",
                }}
                pressed={pressed}
                onClick={onToggle}
              />
              <div css={charLimitCss(isCharacterLimitReached)}>
                {inputValue.length}/{MAX_CHARS} characters
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AIExpandableTextArea;
